import { Platform } from "react-native";
import { showSuccessToast } from "@src/utils/toast";
import { showErrorToast } from "@src/utils/toast";

// Types matching expo-file-system exports
interface FileInstance {
  uri: string;
}

interface DirectoryInstance {
  uri: string;
}

interface FileStatic {
  new (...uris: (string | FileInstance | DirectoryInstance)[]): FileInstance;
  downloadFileAsync(url: string, destination: DirectoryInstance | FileInstance): Promise<FileInstance>;
}

interface PathsStatic {
  cache: DirectoryInstance;
  document: DirectoryInstance;
}

// Types for expo-media-library
interface MediaLibraryPermissionResponse {
  status: "granted" | "denied" | "undetermined";
}

interface MediaLibraryStatic {
  requestPermissionsAsync: () => Promise<MediaLibraryPermissionResponse>;
  saveToLibraryAsync: (uri: string) => Promise<void>;
}

// Module cache
let cachedFile: FileStatic | null = null;
let cachedPaths: PathsStatic | null = null;
let cachedMediaLibrary: MediaLibraryStatic | null = null;

function getNativeModules(): {
  File: FileStatic;
  Paths: PathsStatic;
  MediaLibrary: MediaLibraryStatic;
} {
  if (Platform.OS === "web") {
    throw new Error("Native modules not available on web");
  }

  if (!cachedFile || !cachedPaths || !cachedMediaLibrary) {
    // Dynamic imports for native-only modules
    // Using require to avoid bundling on web
    const fileSystem = require("expo-file-system") as {
      File: FileStatic;
      Paths: PathsStatic;
    };
    cachedMediaLibrary = require("expo-media-library") as MediaLibraryStatic;
    cachedFile = fileSystem.File;
    cachedPaths = fileSystem.Paths;
  }

  return {
    File: cachedFile,
    Paths: cachedPaths,
    MediaLibrary: cachedMediaLibrary,
  };
}

/**
 * Triggers the browser's native download flow rather than opening the video
 * inline in a new tab. `download` only forces a save dialog when the response
 * doesn't set its own `Content-Disposition` (as compilation video URLs now
 * do server-side) — `target="_blank"` is kept as a fallback for URLs without
 * that header, matching the previous open-in-new-tab behaviour.
 */
function downloadForWeb(url: string, filename: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function downloadForNative(url: string, filename: string): Promise<void> {
  const { File, Paths, MediaLibrary } = getNativeModules();

  // Create destination file in cache directory
  const destination = new File(Paths.cache, filename);

  // Delete existing file if it exists to prevent "Destination already exists" error
  try {
    if (destination.uri) {
      const fs = require("expo-file-system");
      const fileInfo = await fs.getInfoAsync(destination.uri);
      if (fileInfo.exists) {
        await fs.deleteAsync(destination.uri, { idempotent: true });
      }
    }
  } catch {
    // Ignore errors - file may not exist
  }

  // Download file using static method
  const downloadedFile = await File.downloadFileAsync(url, destination);

  // Always save to camera roll (clips now delivered as mp4)
  const permissionResult = await MediaLibrary.requestPermissionsAsync();
  if (permissionResult.status !== "granted") {
    throw new Error("Permission denied");
  }
  await MediaLibrary.saveToLibraryAsync(downloadedFile.uri);
  showSuccessToast({ message: "Video saved", description: "Video saved to camera roll" });
}

export interface DownloadVideoOptions {
  videoUrl: string;
  timestamp: Date;
}

export async function downloadVideo({ videoUrl, timestamp }: DownloadVideoOptions): Promise<void> {
  // Extract file extension from URL, default to mp4
  const urlPath = new URL(videoUrl).pathname;
  const extension = urlPath.includes(".") ? urlPath.split(".").pop()?.toLowerCase() : "mp4";

  // Include both clip timestamp and current time to ensure uniqueness
  const filename = `clip_${timestamp.getTime()}_${Date.now()}.${extension}`;

  if (Platform.OS === "web") {
    downloadForWeb(videoUrl, filename);
    showSuccessToast({ message: "Downloading video", description: "Check your browser's downloads" });
  } else {
    await downloadForNative(videoUrl, filename);
  }
}

export function handleDownloadError(error: unknown): void {
  console.error("Download error:", error);
  showErrorToast({ error });
}
